import { Badge } from '@/components/ui/badge';
import { Wrench, PackageOpen, Package, Lock } from 'lucide-react';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card';
import { cn } from '@/lib/utils';

interface JobPhaseBadgeProps {
  phase: 'teardown' | 'parts_delay' | 'assembly' | 'normal';
  partsEta?: string | null;
  bayLocked?: boolean;
  className?: string;
}

export const JobPhaseBadge = ({
  phase,
  partsEta,
  bayLocked,
  className,
}: JobPhaseBadgeProps) => {
  if (phase === 'normal') return null;

  const phaseConfig = {
    teardown: {
      icon: Wrench,
      label: 'Teardown',
      variant: 'secondary' as const,
      description: 'Vehicle disassembly in progress',
      color: 'text-blue-600',
    },
    parts_delay: {
      icon: Package,
      label: 'Waiting for Parts',
      variant: 'secondary' as const,
      description: 'Parts ordered, bay may be locked',
      color: 'text-amber-600',
    },
    assembly: {
      icon: PackageOpen,
      label: 'Assembly',
      variant: 'default' as const,
      description: 'Final assembly & testing',
      color: 'text-green-600',
    },
  };

  const config = phaseConfig[phase];
  const Icon = config.icon;

  const formatEta = (eta: string) => {
    const date = new Date(eta);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  return (
    <HoverCard>
      <HoverCardTrigger asChild>
        <Badge variant={config.variant} className={cn('gap-1 cursor-help', className)}>
          <Icon className="h-3 w-3" />
          <span className="text-xs">{config.label}</span>
          {bayLocked && <Lock className="h-2.5 w-2.5 ml-0.5" />}
        </Badge>
      </HoverCardTrigger>
      <HoverCardContent className="w-72" align="start">
        <div className="space-y-2">
          <div className="flex items-start gap-2">
            <Icon className={`h-4 w-4 mt-0.5 ${config.color}`} />
            <div className="space-y-1">
              <p className="text-sm font-medium">{config.label}</p>
              <p className="text-xs text-muted-foreground">{config.description}</p>
            </div>
          </div>

          {phase === 'parts_delay' && partsEta && (
            <div className="pt-2 border-t">
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Parts ETA:</span>
                <span className="font-medium">{formatEta(partsEta)}</span>
              </div>
            </div>
          )}

          {bayLocked && (
            <div className="pt-2 border-t flex items-start gap-2">
              <Lock className="h-3 w-3 mt-0.5 text-amber-600" />
              <p className="text-xs text-muted-foreground">
                Bay locked - job must resume in same location
              </p>
            </div>
          )}

          <div className="pt-2 border-t text-xs text-muted-foreground">
            {phase === 'teardown' && (
              <p>Disassembly must complete before parts delay begins</p>
            )}
            {phase === 'parts_delay' && (
              <p>Bay may be locked to ensure proper reassembly location</p>
            )}
            {phase === 'assembly' && (
              <p>Final stage - completion imminent</p>
            )}
          </div>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
};
